import type { ComboId, ComboResult, ScoredHand } from './types';

export const COMBO_BASE: Record<ComboId, number> = {
  'pair': 10,
  'two-pair': 25,
  'three-kind': 40,
  'full-house': 70,
  'small-straight': 60,
  'large-straight': 100,
  'four-kind': 120,
  'five-kind': 200,
  'six-kind': 400,
  'chance': 0, // Somma secca: base 0, i chips sono la somma di tutti i pips
};

/** Indici dei dadi raggruppati per faccia, es. {4: [0,3], 6: [1]}. */
function indicesByFace(faces: number[]): Map<number, number[]> {
  const map = new Map<number, number[]>();
  faces.forEach((f, i) => {
    const list = map.get(f) ?? [];
    list.push(i);
    map.set(f, list);
  });
  return map;
}

/**
 * Valore in chips di una faccia: i talismani possono truccarlo ("i 3 valgono 6")
 * e certi dadi speciali pure (il Vetro raddoppia) — per questo riceve anche l'indice.
 */
export type PipValue = (face: number, index: number) => number;
const identityPips: PipValue = (f) => f;

/** Faccia massima possibile (il Dado D8 arriva a 8: le scale si allungano). */
const MAX_FACE = 8;

function make(id: ComboId, usedIndices: number[], faces: number[], pip: PipValue, baseOverride?: number): ComboResult {
  const base = baseOverride ?? COMBO_BASE[id];
  const pips = usedIndices.reduce((sum, i) => sum + pip(faces[i] ?? 0, i), 0);
  return { id, base, usedIndices, chips: base + pips };
}

/** Prova a comporre una scala di `len` facce consecutive; preferisce la finestra più alta. */
function findStraight(byFace: Map<number, number[]>, faces: number[], pip: PipValue, len: number, id: ComboId): ComboResult | null {
  for (let start = MAX_FACE - len + 1; start >= 1; start--) {
    const used: number[] = [];
    for (let f = start; f < start + len; f++) {
      const idx = byFace.get(f)?.[0];
      if (idx === undefined) break;
      used.push(idx);
    }
    if (used.length === len) return make(id, used, faces, pip);
  }
  return null;
}

/**
 * Rileva tutte le combo presenti (la variante migliore di ciascuna),
 * ordinate per chips decrescenti. La Somma secca c'è sempre: fallback garantito.
 */
export function detectCombos(faces: number[], pip: PipValue = identityPips): ComboResult[] {
  const byFace = indicesByFace(faces);
  const results: ComboResult[] = [];
  // Facce con almeno n occorrenze, dalla più alta (chips migliori a parità di combo)
  const facesWithAtLeast = (n: number): number[] =>
    [...byFace.entries()].filter(([, idx]) => idx.length >= n).map(([f]) => f).sort((a, b) => b - a);

  const kinds: Array<[number, ComboId]> = [
    [6, 'six-kind'], [5, 'five-kind'], [4, 'four-kind'], [3, 'three-kind'], [2, 'pair'],
  ];
  for (const [n, id] of kinds) {
    const face = facesWithAtLeast(n)[0];
    if (face !== undefined) results.push(make(id, byFace.get(face)!.slice(0, n), faces, pip));
  }

  // Full: tris + coppia di facce diverse, massimizzando i pips
  let bestFull: ComboResult | null = null;
  for (const triple of facesWithAtLeast(3)) {
    for (const pairFace of facesWithAtLeast(2)) {
      if (pairFace === triple) continue;
      const used = [...byFace.get(triple)!.slice(0, 3), ...byFace.get(pairFace)!.slice(0, 2)];
      const candidate = make('full-house', used, faces, pip);
      if (!bestFull || candidate.chips > bestFull.chips) bestFull = candidate;
    }
  }
  if (bestFull) results.push(bestFull);

  // Doppia coppia: le due facce più alte con almeno una coppia
  const pairFaces = facesWithAtLeast(2);
  if (pairFaces.length >= 2) {
    const used = [...byFace.get(pairFaces[0]!)!.slice(0, 2), ...byFace.get(pairFaces[1]!)!.slice(0, 2)];
    results.push(make('two-pair', used, faces, pip));
  }

  const large = findStraight(byFace, faces, pip, 5, 'large-straight');
  if (large) results.push(large);
  const small = findStraight(byFace, faces, pip, 4, 'small-straight');
  if (small) results.push(small);

  // Somma secca: sempre valida, usa tutti i dadi (base 0 => chips = somma pips)
  results.push(make('chance', faces.map((_, i) => i), faces, pip, 0));

  results.sort((a, b) => b.chips - a.chips || b.base - a.base);
  return results;
}

/** Chiude la mano: combo migliore, chips × mult. Il mult resta 1 finché non arrivano i talismani. */
export function scoreHand(faces: number[]): ScoredHand {
  const all = detectCombos(faces);
  const best = all[0]!;
  const mult = 1;
  return { best, all, chips: best.chips, mult, score: best.chips * mult };
}
